import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import ReactFlow, {
  useNodesState,
  useEdgesState,
  Handle,
  Position,
  Background,
  Controls,
  MiniMap,
} from 'reactflow';
import dagre from 'dagre';
import { 
  Search, 
  Maximize, 
  Minimize, 
  Loader2, 
  ShieldAlert, 
  RefreshCw, 
  Plus, 
  Clock,
  Minus,
  Info
} from 'lucide-react';
import 'reactflow/dist/style.css';
import { useNavigate } from "react-router-dom";

// ==========================================
// CONFIGURATION
// ==========================================
const FETCH_API = 'https://whiteintel.io/api/regular/app/v2/investigations/get_job_graph.php';
const EXPAND_API = 'https://whiteintel.io/api/regular/app/v2/investigations/expand_graph.php';
const NODE_WIDTH = 280;
const NODE_HEIGHT = 100;

const getJwtToken = () => localStorage.getItem("token") || "";

// ==========================================
// 1. CUSTOM NODE COMPONENTS
// ==========================================

const nodeStyle = {
  padding: '12px 16px',
  borderRadius: '12px',
  backgroundColor: '#0f172a',
  border: '1px solid #1e293b',
  color: '#f8fafc',
  minWidth: '260px',
  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)',
  position: 'relative',
};

const SystemNode = ({ data }) => (
  <div style={{ ...nodeStyle, borderLeft: '4px solid #38bdf8', opacity: data.dimmed ? 0.3 : 1 }}>
    <Handle type="target" position={Position.Left} style={{ background: '#38bdf8' }} />
    
    <div className="flex items-center gap-3 mb-2">
      <div className="p-1.5 bg-sky-500/10 rounded-lg text-sky-400">
        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
          <line x1="8" y1="21" x2="16" y2="21"></line>
          <line x1="12" y1="17" x2="12" y2="21"></line>
        </svg>
      </div>
      <span className="font-bold text-sm truncate max-w-[180px]">{data.label}</span>
    </div>

    <div className="flex flex-col gap-1">
      <div className="text-[10px] text-neutral-400 font-mono">{data.ip || '0.0.0.0'}</div>
      <div className="flex items-center gap-2 mt-1">
        {data.country && (
          <img 
            src={`https://flagcdn.com/16x12/${data.country.toLowerCase()}.png`} 
            alt={data.country} 
            className="rounded-sm" 
          />
        )}
        <span className="text-[10px] text-neutral-500 uppercase font-bold">
          {data.os || 'Unknown OS'}
        </span>
      </div>
    </div>

    {/* --- LOG DATE FOOTER --- */}
    {data.log_date && (
      <div className="mt-3 pt-2 border-t border-white/5 flex items-center gap-1.5">
        <Clock size={10} className="text-neutral-600" />
        <span className="text-[9px] text-neutral-500 font-mono">
          {data.log_date}
        </span>
      </div>
    )}

    <div className="absolute top-3 right-3 text-sky-500/50">
      {data.isExpanded ? <Minus size={14} /> : <Plus size={14} />}
    </div>
    
    <Handle type="source" position={Position.Right} style={{ background: '#38bdf8' }} />
  </div>
);

const AppNode = ({ data }) => (
  <div style={{ 
    ...nodeStyle, 
    borderLeft: '4px solid #a855f7', // Violet-500
    opacity: data.dimmed ? 0.3 : 1,
    cursor: 'default' // Change cursor to default since it's not clickable for expansion
  }}>
    <Handle type="target" position={Position.Left} style={{ background: '#a855f7' }} />
    
    <div className="flex items-center gap-3">
      <div className="p-1.5 bg-violet-500/10 rounded-lg text-violet-400">
        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"></path>
        </svg>
      </div>
      <div className="flex flex-col">
        <span className="font-bold text-sm truncate max-w-[180px] text-violet-100">
          {data.label}
        </span>
        <span className="text-[9px] uppercase tracking-widest text-neutral-500 font-black">
          Application
        </span>
      </div>
    </div>

    {/* Optional: Indicator for Sensitive Categories */}
    {data.category && (
      <div className="mt-2 flex items-center gap-1">
        <div className="w-1 h-1 rounded-full bg-violet-500 animate-pulse" />
        <span className="text-[8px] text-neutral-400 uppercase font-bold tracking-tighter">
          End-of-chain Node
        </span>
      </div>
    )}

    {/* Note: The Plus/Minus absolute div has been removed here */}
    
    <Handle type="source" position={Position.Right} style={{ background: '#a855f7', opacity: 0 }} />
  </div>
);
const UserNode = ({ data, type }) => (
  <div style={{ 
    ...nodeStyle, 
    borderLeft: type === 'user' ? '4px solid #ef4444' : '4px solid #fbbf24', 
    opacity: data.dimmed ? 0.3 : 1 
  }}>
    <Handle type="target" position={Position.Left} />
    <div className="flex items-center gap-3">
      <div className={`p-1.5 rounded-lg ${type === 'user' ? 'bg-red-500/10 text-red-400' : 'bg-amber-500/10 text-amber-400'}`}>
        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
      </div>
      <div className="flex flex-col">
        <span className="font-bold text-sm truncate max-w-[180px]">{data.label}</span>
        <span className="text-[9px] uppercase tracking-widest text-neutral-500 font-black">
          {type === 'user' ? 'Target Identity' : 'Related Account'}
        </span>
      </div>
    </div>
    <Handle type="source" position={Position.Right} />
  </div>
);

// ==========================================
// 2. LAYOUT ENGINE (Dagre)
// ==========================================
const getLayoutedElements = (nodes, edges) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: 'LR', nodesep: 70, ranksep: 120 });

  nodes.forEach((node) => dagreGraph.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT }));
  edges.forEach((edge) => dagreGraph.setEdge(edge.source, edge.target));

  dagre.layout(dagreGraph);

  return {
    nodes: nodes.map((node) => {
      const pos = dagreGraph.node(node.id);
      return { ...node, position: { x: pos.x - NODE_WIDTH / 2, y: pos.y - NODE_HEIGHT / 2 } };
    }),
    edges
  };
};

// ==========================================
// 3. MAIN COMPONENT
// ==========================================
const InvestigationGraph = ({ jobId, status }) => {
  const navigate = useNavigate();
  const graphContainerRef = useRef(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isFullscreen, setIsFullscreen] = useState(false);

  const nodeTypes = useMemo(() => ({
    system: SystemNode,
    pivot: (props) => <UserNode {...props} type="pivot" />,
    user: (props) => <UserNode {...props} type="user" />,
    app: AppNode
  }), []);

  // --- INITIAL SNAPSHOT FETCH ---
const loadInitialSnapshot = useCallback(async () => {
  if (!jobId || status !== 'completed') return;
  setLoading(true);
  try {
    const res = await fetch(FETCH_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getJwtToken()}` },
      body: JSON.stringify({ job_id: jobId }),
    });
    const json = await res.json();
    
    // Check if graph_data exists and has nodes
    if (json.success && json.graph_data && json.graph_data.nodes.length > 0) {
      const { nodes: rawNodes, links: rawLinks } = json.graph_data;
      
      const rfNodes = rawNodes.map(n => ({
        id: n.id,
        type: n.type,
        data: { label: n.label, ...n.details, isExpanded: false }
      }));
      
      const rfEdges = rawLinks.map((l, i) => ({
        id: `e-${i}`,
        source: l.source,
        target: l.target,
        animated: true,
        style: { stroke: '#334155', strokeWidth: 2 }
      }));

      const layouted = getLayoutedElements(rfNodes, rfEdges);
      setNodes(layouted.nodes);
      setEdges(layouted.edges);
    } else {
      // Explicitly set nodes to empty if success but no data
      setNodes([]); 
    }
  } catch (err) {
    console.error("Snapshot error:", err);
  } finally {
    setLoading(false);
  }
}, [jobId, status, setNodes, setEdges]);

useEffect(() => {
  if (status === 'completed' && nodes.length === 0) {
    loadInitialSnapshot();
  }
}, [status, nodes.length, loadInitialSnapshot]);

  // --- EXPANSION (HYBRID DEEP DIVE) ---
  const onNodeClick = useCallback(async (event, node) => {
    // Only expand if it's a system node (following previous logic)
    if (node.type !== 'system' && node.type !== 'pivot' && node.type !== 'user') return;

    setLoading(true);
    try {
      const res = await fetch(EXPAND_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getJwtToken()}` },
        body: JSON.stringify({ 
            job_id: jobId,
            node_id: node.id, 
            node_type: node.type,
            label: node.data.label 
        }),
      });
      const json = await res.json();

      if (json.success) {
        // Mark the clicked node as expanded
        const updatedNodesBase = nodes.map(n => 
          n.id === node.id ? { ...n, data: { ...n.data, isExpanded: true }} : n
        );

        const newNodes = (json.new_nodes || [])
          .filter(nn => !nodes.find(existing => existing.id === nn.id))
          .map(nn => ({ 
            id: nn.id, 
            type: nn.type, 
            data: { label: nn.label, isExpanded: false }
          }));

        const newEdges = (json.new_links || []).map((nl, i) => ({
          id: `e-ex-${Date.now()}-${i}`,
          source: nl.source,
          target: nl.target,
          animated: true,
          style: { stroke: '#475569' }
        }));

        const layouted = getLayoutedElements(
          [...updatedNodesBase, ...newNodes], 
          [...edges, ...newEdges]
        );
        setNodes(layouted.nodes);
        setEdges(layouted.edges);
      }
    } catch (err) {
      console.error("Expansion error:", err);
    } finally {
      setLoading(false);
    }
  }, [nodes, edges, jobId]);

  // --- SEARCH HIGHLIGHTING ---
  useEffect(() => {
    setNodes(nds => nds.map(n => ({
      ...n,
      data: { 
        ...n.data, 
        dimmed: searchQuery && !n.data.label.toLowerCase().includes(searchQuery.toLowerCase()) 
      }
    })));
  }, [searchQuery, setNodes]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      graphContainerRef.current.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // --- RENDER STATES ---
// 1. Loading/Processing
if (status === 'pending' || status === 'processing') {
  return (
    <div className="flex flex-col items-center justify-center h-full bg-[#000414]">
      <div className="relative mb-6">
        <div className="absolute inset-0 bg-sky-500/20 blur-2xl rounded-full animate-pulse" />
        <Loader2 className="w-12 h-12 text-sky-500 animate-spin relative z-10" />
      </div>
      <p className="text-[10px] uppercase tracking-[0.3em] font-black text-neutral-500">Mapping Forensic Nodes</p>
    </div>
  );
}

// 2. Failed
if (status === 'failed') {
  return (
    <div className="flex flex-col items-center justify-center h-full bg-[#000414] p-8 text-center">
      <ShieldAlert className="w-16 h-16 text-red-500/40 mb-4" />
      <h3 className="text-white font-bold uppercase tracking-widest text-sm">Graph Reconstruction Interrupted</h3>
      <p className="text-neutral-500 text-xs mt-2 max-w-xs">Data density exceeded threshold for visual render.</p>
    </div>
  );
}

// 3. Empty State (Add this new check)
if (status === 'completed' && !loading && nodes.length === 0) {
  return (
    <div className="flex flex-col items-center justify-center h-full bg-[#000414] p-8 text-center border border-dashed border-white/5 rounded-2xl m-4">
      <div className="p-4 bg-white/5 rounded-full mb-4">
        <Info className="w-8 h-8 text-neutral-600" />
      </div>
      <h3 className="text-white font-bold uppercase tracking-widest text-xs">No Lateral Connections</h3>
      <p className="text-neutral-500 text-[11px] mt-2 max-w-[280px]">
        The neural engine found no secondary pivots or infected systems linked to this target identity.
      </p>
      <button 
        onClick={loadInitialSnapshot} 
        className="mt-6 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-[10px] font-bold uppercase tracking-widest text-neutral-400 transition-all"
      >
        Re-scan Dataset
      </button>
    </div>
  );
}

  return (
    <div ref={graphContainerRef} className="w-full h-full relative bg-[#06101e] select-none">
      
      {/* OVERLAYS */}
      {loading && (
        <div className="absolute top-6 left-1/2 -translate-x-1/2 z-50 bg-sky-500 text-white px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-2xl">
          <RefreshCw className="w-3 h-3 animate-spin" /> Deep Search Active
        </div>
      )}

      {/* TOP BAR */}
      <div className="absolute top-6 left-6 z-10 flex gap-3">
        <div className="relative group">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500 group-focus-within:text-sky-400 transition-colors" />
          <input 
            type="text" 
            placeholder="Search host, user or IP..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-[#0f172a] border border-white/10 rounded-xl pl-10 pr-4 py-2 text-xs outline-none focus:border-sky-500/50 w-72 text-white shadow-xl"
          />
        </div>
      </div>

      <div className="absolute top-6 right-6 z-10 flex gap-2">
        <button 
          onClick={toggleFullscreen} 
          className="p-2.5 bg-[#0f172a] border border-white/10 rounded-xl text-neutral-400 hover:text-sky-400 hover:border-sky-500/30 transition-all shadow-xl"
        >
          {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
        </button>
        <button 
          onClick={loadInitialSnapshot} 
          className="p-2.5 bg-[#0f172a] border border-white/10 rounded-xl text-neutral-400 hover:text-sky-400 hover:border-sky-500/30 transition-all shadow-xl"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {/* LEGEND */}
      <div className="absolute bottom-6 left-6 z-10 bg-[#0f172a]/80 backdrop-blur-md border border-white/10 rounded-xl p-3 flex flex-col gap-2 shadow-2xl">
        <div className="flex items-center gap-2 text-[10px] font-bold text-neutral-400 uppercase tracking-wider">
          <div className="w-2 h-2 rounded-full bg-red-500" /> Target
        </div>
        <div className="flex items-center gap-2 text-[10px] font-bold text-neutral-400 uppercase tracking-wider">
          <div className="w-2 h-2 rounded-full bg-sky-500" /> Infected System
        </div>
        <div className="flex items-center gap-2 text-[10px] font-bold text-neutral-400 uppercase tracking-wider">
          <div className="w-2 h-2 rounded-full bg-amber-500" /> Related Account
        </div>
        <div className="flex items-center gap-2 text-[10px] font-bold text-neutral-400 uppercase tracking-wider">
    <div className="w-2 h-2 rounded-full bg-violet-500" /> Application/Service
  </div>
      </div>

      

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        nodeTypes={nodeTypes}
        fitView
        minZoom={0.2}
        maxZoom={1.5}
      >
        <Background color="#1e293b" gap={25} size={1} />
        <Controls className="bg-[#0f172a] border-white/10 fill-white !shadow-none !border" />
        <MiniMap 
          className="!bg-[#0f172a] !border-white/10 !rounded-xl"
          nodeStrokeColor={(n) => n.type === 'system' ? '#38bdf8' : '#fbbf24'}
          nodeColor="#0f172a"
          maskColor="rgba(15, 23, 42, 0.6)"
        />
      </ReactFlow>
    </div>
  );
};

export default InvestigationGraph;